import React, { useState, useEffect } from 'react';
import {
    Box, Button, Paper, Typography, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Alert, Snackbar, CircularProgress, Avatar, Select, MenuItem, FormControl, InputLabel, Chip
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Close as CloseIcon, AddPhotoAlternate } from '@mui/icons-material';
import { getCategories, getSubCategories, getProducts, createProduct, updateProduct, deleteProduct } from '../../../services/firestoreService';
import { uploadImage, deleteImage } from '../../../services/storageService';
import { COLORS } from '../../../assets/colors';

function ProductsManager() {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [subCategories, setSubCategories] = useState([]);
    const [filteredSubCategories, setFilteredSubCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [currentProduct, setCurrentProduct] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        categoryId: '',
        subCategoryId: '',
        image: '',
        imageFile: null,
        details: [],
        detailsFiles: [],
        position: 0
    });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        const [prodsResult, catsResult, subCatsResult] = await Promise.all([
            getProducts(),
            getCategories(),
            getSubCategories()
        ]);
        if (prodsResult.success) setProducts(prodsResult.data);
        if (catsResult.success) setCategories(catsResult.data);
        if (subCatsResult.success) setSubCategories(subCatsResult.data);
        setLoading(false);
    };

    const handleOpenDialog = (product = null) => {
        if (product) {
            setEditMode(true);
            setCurrentProduct(product);
            setFormData({
                name: product.name,
                description: product.description,
                categoryId: product.categoryId,
                subCategoryId: product.subCategoryId,
                image: product.image,
                imageFile: null,
                details: product.details || [],
                detailsFiles: [],
                position: product.position
            });
            filterSubCategories(product.categoryId);
        } else {
            setEditMode(false);
            setCurrentProduct(null);
            setFormData({
                name: '',
                description: '',
                categoryId: '',
                subCategoryId: '',
                image: '',
                imageFile: null,
                details: [],
                detailsFiles: [],
                position: products.length
            });
        }
        setOpenDialog(true);
    };

    const filterSubCategories = (categoryId) => {
        const filtered = subCategories.filter(sub => sub.categoryId === categoryId);
        setFilteredSubCategories(filtered);
    };

    const handleCategoryChange = (categoryId) => {
        setFormData({ ...formData, categoryId, subCategoryId: '' });
        filterSubCategories(categoryId);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setFormData({
            name: '',
            description: '',
            categoryId: '',
            subCategoryId: '',
            image: '',
            imageFile: null,
            details: [],
            detailsFiles: [],
            position: 0
        });
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file && file.type.startsWith('image/')) {
            setFormData({ ...formData, imageFile: file });
        }
    };

    const handleDetailsImagesChange = (e) => {
        const files = Array.from(e.target.files);
        const newFiles = files.map(file => ({
            file: file,
            title: file.name.replace(/\.[^/.]+$/, '') // Nombre del archivo sin extensión
        }));
        setFormData({ ...formData, detailsFiles: [...formData.detailsFiles, ...newFiles] });
    };

    const handleRemoveExistingDetail = (index) => {
        const updatedDetails = [...formData.details];
        updatedDetails.splice(index, 1);
        setFormData({ ...formData, details: updatedDetails });
    };

    const handleRemoveNewDetail = (index) => {
        const updatedFiles = [...formData.detailsFiles];
        updatedFiles.splice(index, 1);
        setFormData({ ...formData, detailsFiles: updatedFiles });
    };

    const handleUpdateDetailTitle = (index, newTitle) => {
        const updatedDetails = [...formData.details];
        updatedDetails[index].title = newTitle;
        setFormData({ ...formData, details: updatedDetails });
    };

    const handleUpdateNewDetailTitle = (index, newTitle) => {
        const updatedFiles = [...formData.detailsFiles];
        updatedFiles[index].title = newTitle;
        setFormData({ ...formData, detailsFiles: updatedFiles });
    };

    const handleSubmit = async () => {
        if (!formData.name || !formData.categoryId || !formData.subCategoryId) {
            showSnackbar('Nombre, categoría y subcategoría son requeridos', 'error');
            return;
        }

        setSubmitting(true);
        try {
            let imageUrl = formData.image;

            // Subir imagen principal
            if (formData.imageFile) {
                const uploadResult = await uploadImage(formData.imageFile, 'RosalStorage/Productos');
                if (uploadResult.success) {
                    imageUrl = uploadResult.url;
                    if (editMode && formData.image && formData.image !== imageUrl) {
                        await deleteImage(formData.image);
                    }
                } else {
                    showSnackbar('Error al subir la imagen', 'error');
                    setSubmitting(false);
                    return;
                }
            }

            // Eliminar imágenes de detalles que se quitaron (en modo edición)
            if (editMode && currentProduct.details) {
                const removedDetails = currentProduct.details.filter(
                    oldDetail => !formData.details.find(newDetail => newDetail.image === oldDetail.image)
                );
                for (const removedDetail of removedDetails) {
                    if (removedDetail.image) {
                        await deleteImage(removedDetail.image);
                    }
                }
            }

            // Subir nuevas imágenes de detalles
            let details = [...formData.details];
            if (formData.detailsFiles.length > 0) {
                for (const fileData of formData.detailsFiles) {
                    const uploadResult = await uploadImage(fileData.file, 'RosalStorage/Productos');
                    if (uploadResult.success) {
                        details.push({
                            title: fileData.title || `${formData.name} - Detalle`,
                            image: uploadResult.url
                        });
                    }
                }
            }

            const productData = {
                name: formData.name,
                description: formData.description,
                categoryId: formData.categoryId,
                subCategoryId: formData.subCategoryId,
                image: imageUrl,
                details: details,
                position: formData.position
            };

            const result = editMode
                ? await updateProduct(currentProduct.id, productData)
                : await createProduct(productData);

            if (result.success) {
                showSnackbar(editMode ? 'Producto actualizado' : 'Producto creado', 'success');
                handleCloseDialog();
                loadData();
            } else {
                showSnackbar('Error al guardar', 'error');
            }
        } catch (error) {
            showSnackbar('Error inesperado', 'error');
        }
        setSubmitting(false);
    };

    const handleDelete = async (product) => {
        if (window.confirm(`¿Eliminar "${product.name}"?`)) {
            const result = await deleteProduct(product.id);
            if (result.success) {
                if (product.image) await deleteImage(product.image);
                if (product.details) {
                    for (const detail of product.details) {
                        if (detail.image) await deleteImage(detail.image);
                    }
                }
                showSnackbar('Producto eliminado', 'success');
                loadData();
            } else {
                showSnackbar('Error al eliminar', 'error');
            }
        }
    };

    const showSnackbar = (message, severity) => setSnackbar({ open: true, message, severity });

    const getCategoryName = (id) => categories.find(c => c.id === id)?.name || 'N/A';
    const getSubCategoryName = (id) => subCategories.find(s => s.id === id)?.name || 'N/A';

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                <Typography variant="h4" sx={{ fontFamily: 'Quicksand', fontWeight: 'bold', color: COLORS.primary }}>
                    Gestión de Productos
                </Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}
                    sx={{ background: '#17a2b8', fontFamily: 'Quicksand', fontWeight: 'bold', textTransform: 'none', borderRadius: '30px' }}>
                    Nuevo Producto
                </Button>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}><CircularProgress /></Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead sx={{ background: '#17a2b8' }}>
                            <TableRow>
                                <TableCell sx={{ color: 'white', fontFamily: 'Quicksand', fontWeight: 'bold' }}>Imagen</TableCell>
                                <TableCell sx={{ color: 'white', fontFamily: 'Quicksand', fontWeight: 'bold' }}>Nombre</TableCell>
                                <TableCell sx={{ color: 'white', fontFamily: 'Quicksand', fontWeight: 'bold' }}>Descripción</TableCell>
                                <TableCell sx={{ color: 'white', fontFamily: 'Quicksand', fontWeight: 'bold' }}>Categoría</TableCell>
                                <TableCell sx={{ color: 'white', fontFamily: 'Quicksand', fontWeight: 'bold' }}>Subcategoría</TableCell>
                                <TableCell sx={{ color: 'white', fontFamily: 'Quicksand', fontWeight: 'bold' }}>Detalles</TableCell>
                                <TableCell sx={{ color: 'white', fontFamily: 'Quicksand', fontWeight: 'bold' }} align="right">Acciones</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {products.map((product) => (
                                <TableRow key={product.id} hover>
                                    <TableCell><Avatar src={product.image} sx={{ width: 60, height: 60, borderRadius: 2 }} /></TableCell>
                                    <TableCell sx={{ fontFamily: 'Quicksand', fontWeight: 600 }}>{product.name}</TableCell>
                                    <TableCell sx={{ fontFamily: 'Quicksand', maxWidth: 200 }}>{product.description}</TableCell>
                                    <TableCell sx={{ fontFamily: 'Quicksand' }}>{getCategoryName(product.categoryId)}</TableCell>
                                    <TableCell sx={{ fontFamily: 'Quicksand' }}>{getSubCategoryName(product.subCategoryId)}</TableCell>
                                    <TableCell>
                                        <Chip label={`${product.details?.length || 0} imágenes`} size="small" sx={{ fontFamily: 'Quicksand' }} />
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton onClick={() => handleOpenDialog(product)} sx={{ color: '#17a2b8' }}><EditIcon /></IconButton>
                                        <IconButton onClick={() => handleDelete(product)} sx={{ color: '#dc3545' }}><DeleteIcon /></IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontFamily: 'Quicksand', fontWeight: 'bold', color: COLORS.primary, display: 'flex', justifyContent: 'space-between' }}>
                    {editMode ? 'Editar Producto' : 'Nuevo Producto'}
                    <IconButton onClick={handleCloseDialog}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ paddingTop: 2 }}>
                        <TextField fullWidth label="Código/Nombre" value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            sx={{ marginBottom: 2 }} InputLabelProps={{ sx: { fontFamily: 'Quicksand' } }} InputProps={{ sx: { fontFamily: 'Quicksand' } }}
                        />
                        <TextField fullWidth multiline rows={3} label="Descripción" value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            sx={{ marginBottom: 2 }} InputLabelProps={{ sx: { fontFamily: 'Quicksand' } }} InputProps={{ sx: { fontFamily: 'Quicksand' } }}
                        />
                        <FormControl fullWidth sx={{ marginBottom: 2 }}>
                            <InputLabel sx={{ fontFamily: 'Quicksand' }}>Categoría</InputLabel>
                            <Select value={formData.categoryId} onChange={(e) => handleCategoryChange(e.target.value)} sx={{ fontFamily: 'Quicksand' }}>
                                {categories.map((cat) => (
                                    <MenuItem key={cat.id} value={cat.id} sx={{ fontFamily: 'Quicksand' }}>{cat.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth sx={{ marginBottom: 2 }} disabled={!formData.categoryId}>
                            <InputLabel sx={{ fontFamily: 'Quicksand' }}>Subcategoría</InputLabel>
                            <Select value={formData.subCategoryId} onChange={(e) => setFormData({ ...formData, subCategoryId: e.target.value })} sx={{ fontFamily: 'Quicksand' }}>
                                {filteredSubCategories.map((sub) => (
                                    <MenuItem key={sub.id} value={sub.id} sx={{ fontFamily: 'Quicksand' }}>{sub.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField fullWidth type="number" label="Posición" value={formData.position}
                            onChange={(e) => setFormData({ ...formData, position: parseInt(e.target.value) })}
                            sx={{ marginBottom: 2 }} InputLabelProps={{ sx: { fontFamily: 'Quicksand' } }} InputProps={{ sx: { fontFamily: 'Quicksand' } }}
                        />
                        <Button variant="outlined" component="label" fullWidth sx={{ marginBottom: 2, fontFamily: 'Quicksand', textTransform: 'none' }}>
                            {formData.imageFile ? formData.imageFile.name : 'Imagen Principal'}
                            <input type="file" hidden accept="image/*" onChange={handleImageChange} />
                        </Button>
                        {(formData.image || formData.imageFile) && (
                            <Box sx={{ marginBottom: 2, display: 'flex', justifyContent: 'center' }}>
                                <Avatar src={formData.imageFile ? URL.createObjectURL(formData.imageFile) : formData.image} sx={{ width: 120, height: 120, borderRadius: 2 }} />
                            </Box>
                        )}
                        <Button variant="outlined" component="label" fullWidth startIcon={<AddPhotoAlternate />} sx={{ marginBottom: 2, fontFamily: 'Quicksand', textTransform: 'none' }}>
                            Añadir Imágenes de Detalles
                            <input type="file" hidden accept="image/*" multiple onChange={handleDetailsImagesChange} />
                        </Button>

                        {/* Mostrar imágenes de detalles existentes */}
                        {formData.details.length > 0 && (
                            <Box sx={{ marginBottom: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontFamily: 'Quicksand', fontWeight: 'bold', marginBottom: 1 }}>
                                    Imágenes Actuales ({formData.details.length})
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {formData.details.map((detail, index) => (
                                        <Box key={index} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', padding: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                                            <Box sx={{ position: 'relative' }}>
                                                <Avatar
                                                    src={detail.image}
                                                    sx={{ width: 100, height: 100, borderRadius: 2 }}
                                                />
                                                <IconButton
                                                    onClick={() => handleRemoveExistingDetail(index)}
                                                    sx={{
                                                        position: 'absolute',
                                                        top: -8,
                                                        right: -8,
                                                        backgroundColor: '#dc3545',
                                                        color: 'white',
                                                        width: 28,
                                                        height: 28,
                                                        '&:hover': { backgroundColor: '#c82333' }
                                                    }}
                                                    size="small"
                                                >
                                                    <CloseIcon sx={{ fontSize: 18 }} />
                                                </IconButton>
                                            </Box>
                                            <TextField
                                                fullWidth
                                                label="Título de la imagen"
                                                value={detail.title}
                                                onChange={(e) => handleUpdateDetailTitle(index, e.target.value)}
                                                InputLabelProps={{ sx: { fontFamily: 'Quicksand' } }}
                                                InputProps={{ sx: { fontFamily: 'Quicksand' } }}
                                                size="small"
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        )}

                        {/* Mostrar nuevas imágenes seleccionadas */}
                        {formData.detailsFiles.length > 0 && (
                            <Box sx={{ marginBottom: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontFamily: 'Quicksand', fontWeight: 'bold', marginBottom: 1, color: '#17a2b8' }}>
                                    Nuevas Imágenes ({formData.detailsFiles.length})
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {formData.detailsFiles.map((fileData, index) => (
                                        <Box key={index} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', padding: 2, border: '2px solid #17a2b8', borderRadius: 2, backgroundColor: '#f0f9ff' }}>
                                            <Box sx={{ position: 'relative' }}>
                                                <Avatar
                                                    src={URL.createObjectURL(fileData.file)}
                                                    sx={{ width: 100, height: 100, borderRadius: 2 }}
                                                />
                                                <IconButton
                                                    onClick={() => handleRemoveNewDetail(index)}
                                                    sx={{
                                                        position: 'absolute',
                                                        top: -8,
                                                        right: -8,
                                                        backgroundColor: '#dc3545',
                                                        color: 'white',
                                                        width: 28,
                                                        height: 28,
                                                        '&:hover': { backgroundColor: '#c82333' }
                                                    }}
                                                    size="small"
                                                >
                                                    <CloseIcon sx={{ fontSize: 18 }} />
                                                </IconButton>
                                            </Box>
                                            <TextField
                                                fullWidth
                                                label="Título de la imagen"
                                                value={fileData.title}
                                                onChange={(e) => handleUpdateNewDetailTitle(index, e.target.value)}
                                                InputLabelProps={{ sx: { fontFamily: 'Quicksand' } }}
                                                InputProps={{ sx: { fontFamily: 'Quicksand' } }}
                                                size="small"
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ padding: 3 }}>
                    <Button onClick={handleCloseDialog} sx={{ fontFamily: 'Quicksand', fontWeight: 'bold', textTransform: 'none' }}>Cancelar</Button>
                    <Button onClick={handleSubmit} variant="contained" disabled={submitting}
                        sx={{ background: '#17a2b8', fontFamily: 'Quicksand', fontWeight: 'bold', textTransform: 'none' }}>
                        {submitting ? 'Guardando...' : (editMode ? 'Actualizar' : 'Crear')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                <Alert severity={snackbar.severity} sx={{ fontFamily: 'Quicksand', fontWeight: 600 }}>{snackbar.message}</Alert>
            </Snackbar>
        </Box>
    );
}

export default ProductsManager;
